import axios from "axios";
import { toast } from "react-toastify";
import moment from "moment-timezone";
import { covertTimeSatmp } from "../services/trackerServices.js";
import TimestampConverter from "../helpers/TimestampConverter.jsx";

/**
 * API Helpers for CardBody Component
 * Contains API call functions for duplicate, delete, and update operations
 */

/**
 * Duplicate a timer entry
 * @param {Object} params - Parameters for duplication
 * @returns {Promise} API response
 */
export const duplicateTimerEntry = async ({
  task,
  project_id,
  tag_id,
  client,
  clientId,
  date,
  startTime,
  dupendTime,
  duration,
  sprint_id,
  _id,
  billable,
  selectedUser,
  token,
  dispatch,
  setTimerData,
  setDuplicateLoading,
  setIsUpdateLoading,
  setActiveDropdownId,
}) => {
  // Normalize clientId - extract _id if it's an object, otherwise use as is
  const normalizedClientId = clientId && typeof clientId === 'object' && clientId._id 
    ? clientId._id 
    : clientId;
  
  const postData = {
    task: task,
    project: project_id,
    date: dupendTime,
    tag: tag_id,
    billable: billable,
    startTime: startTime,
    endTime: dupendTime,
    duration: duration,
    client: client,
    clientId: normalizedClientId,
    user: selectedUser,
    id: _id,
    tz: global.config.getTimeZone(),
  };
  if (sprint_id) {
    postData.sprint = sprint_id;
  }
  setDuplicateLoading(true);
  try {
    await axios.post("/api/time-track/duplicate", postData, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    await dispatch(setTimerData(Date.now()));
    global.config.activityLog(window.location.href, "Timer", `Duplicate created Successfully`);
    toast("Duplicate created Successfully", {
      position: "bottom-right",
      autoClose: 2000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
    });
    setIsUpdateLoading(false);
  } catch (error) {
    console.error("Error duplicating timer entry:", error);
    global.config.slackMessage(error.toString());
    toast("Failed to duplicate entry. Please try again.", {
      position: "bottom-right",
      autoClose: 2000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
    });
  } finally {
    setDuplicateLoading(false);
    setActiveDropdownId(null);
  }
};

/**
 * Delete a timer entry
 * @param {Object} params - Parameters for deletion
 * @returns {Promise} API response
 */
export const deleteTimerEntry = async ({
  taskId,
  group,
  selectedUser,
  token,
  dispatch,
  setTimerData,
  setIsDeleteLoading,
  setDeleteId,
  setExpandedGroups,
  handleDropdownToggle,
}) => {
  setIsDeleteLoading(true);
  setDeleteId(taskId);
  var userId = selectedUser;
  await axios
    .delete(`/api/time-track/delete-timer/${taskId}/${userId}`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
    .then(async (response) => {
      toast("Deleted Successfully", {
        position: "bottom-right",
        autoClose: 2000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
      setIsDeleteLoading(false);
      await dispatch(setTimerData(Date.now()));
      // Check if group entries length is 2 and reset expandedGroups
      if (group.entries.length === 2) {
        setExpandedGroups([]);
      }
    })
    .catch((error) => {
      console.log(error);
      global.config.slackMessage(error.toString());
      setIsDeleteLoading(false);
    });

  handleDropdownToggle();
};

/**
 * Update a timer entry
 * @param {Object} params - Parameters for update
 * @returns {Promise} API response
 */
export const updateTimerEntry = async ({
  event,
  editedId,
  taskNameEdit,
  projectIdEdit,
  dateEdit,
  startTimeEdit,
  endTimeEdit,
  dateOldEdit,
  tagIdEdit,
  billableEdit,
  uDuration,
  sprintIdEdit,
  clientEdit,
  clientIdEdit,
  selectedUser,
  token,
  dispatch,
  setTimerData,
  setExpandedGroups,
  setIsUpdateLoading,
  setUEndTimeError,
  setUStartTimeError,
  effectiveSetEditedId,
  setTaskNameEdit,
  setProjectIdEdit,
  setProjectNameEdit,
  setSprintIdEdit,
  setSprintNameEdit,
  setDateEdit,
  setStartTimeEdit,
  setEndTimeEdit,
  setStartTimeEdit24,
  setEndTimeEdit24,
  setClientEdit,
  setClientIdEdit,
  setBillableEdit,
  setTagEdit,
  setTagQuery,
  refetchTimerData,
  toast,
}) => {
  event.preventDefault();
  if (!editedId) {
    toast("Missing Timer ID", { position: "bottom-right", autoClose: 2000 });
    return;
  }
  if (!taskNameEdit) {
    toast("Task name is required", { position: "bottom-right", autoClose: 2000 });
    return;
  }
  if (!projectIdEdit) {
    toast("Project is required", { position: "bottom-right", autoClose: 2000 });
    return;
  }
  if (!dateEdit) {
    toast("Date is required", { position: "bottom-right", autoClose: 2000 });
    return;
  }
  if (!startTimeEdit || !startTimeEdit.trim()) {
    toast("Start time is required", { position: "bottom-right", autoClose: 2000 });
    return;
  }
  if (!endTimeEdit || !endTimeEdit.trim()) {
    toast("End time is required", { position: "bottom-right", autoClose: 2000 });
    return;
  }
  if (!clientEdit) {
    toast("Client is required", { position: "bottom-right", autoClose: 2000 });
    return;
  }
  if (!sprintIdEdit) {
    toast("Sprint is required", { position: "bottom-right", autoClose: 2000 });
    return;
  }
  const id = editedId;
  const task = taskNameEdit;

  // Validate and format date
  if (!dateEdit || (dateEdit instanceof Date && isNaN(dateEdit.getTime()))) {
    toast("Invalid date", { position: "bottom-right", autoClose: 2000 });
    setIsUpdateLoading(false);
    return;
  }

  const date = new Date(dateEdit);
  if (isNaN(date.getTime())) {
    toast("Invalid date format", { position: "bottom-right", autoClose: 2000 });
    setIsUpdateLoading(false);
    return;
  }

  // Ensure moment object is valid before formatting
  // Handle both Date objects and string dates
  let dateMoment;
  if (dateEdit instanceof Date) {
    // If it's a Date object, convert to moment in the specified timezone
    dateMoment = moment(dateEdit).tz(global.config.getTimeZone());
  } else if (typeof dateEdit === 'string') {
    // If it's a string, try parsing it - moment.tz can parse strings
    dateMoment = moment.tz(dateEdit, global.config.getTimeZone());
    // If that fails, try parsing as regular moment then converting to timezone
    if (!dateMoment.isValid()) {
      dateMoment = moment(dateEdit).tz(global.config.getTimeZone());
    }
  } else {
    toast("Invalid date format", { position: "bottom-right", autoClose: 2000 });
    setIsUpdateLoading(false);
    return;
  }

  if (!dateMoment.isValid()) {
    toast("Invalid date format", { position: "bottom-right", autoClose: 2000 });
    setIsUpdateLoading(false);
    return;
  }

  // Format startTime and endTime in the format expected by server: "YYYY-MM-DD, h:mm:ss A"
  // The server expects: moment.tz(startTime, "YYYY-MM-DD, h:mm:ss A", tz)
  let startTimeFormated = null;
  let endTimeFormated = null;

  if (startTimeEdit && startTimeEdit.trim()) {
    // Get the date part in YYYY-MM-DD format
    const datePart = dateMoment.format("YYYY-MM-DD");
    // Combine with time: "YYYY-MM-DD, h:mm:ss A"
    startTimeFormated = `${datePart}, ${startTimeEdit.trim()}`;
  }

  if (endTimeEdit && endTimeEdit.trim()) {
    // Get the date part in YYYY-MM-DD format
    const datePart = dateMoment.format("YYYY-MM-DD");
    // Combine with time: "YYYY-MM-DD, h:mm:ss A"
    endTimeFormated = `${datePart}, ${endTimeEdit.trim()}`;
  }

  const startdate = dateMoment.utc().format("YYYY-MM-DD HH:mm:ss");

  // Validate formatted date string - check for invalid date strings
  if (!startdate ||
    typeof startdate !== 'string' ||
    startdate === "Invalid date" ||
    startdate.includes("NaN") ||
    startdate.includes("Invalid") ||
    startdate.length !== 19) { // "YYYY-MM-DD HH:mm:ss" should be exactly 19 characters
    toast("Invalid date format", { position: "bottom-right", autoClose: 2000 });
    setIsUpdateLoading(false);
    return;
  }

  // Additional validation: ensure the date string can be parsed and is a valid date
  const testDate = new Date(startdate.replace(' ', 'T') + 'Z'); // Convert to ISO format for parsing
  if (isNaN(testDate.getTime())) {
    toast("Invalid date format", { position: "bottom-right", autoClose: 2000 });
    setIsUpdateLoading(false);
    return;
  }

  // Ensure startdate is definitely a string (not accidentally converted to number)
  const dateString = String(startdate);

  // Final validation - ensure all required fields are valid
  if (!dateString || typeof dateString !== 'string' || dateString.length !== 19) {
    toast("Invalid date format", { position: "bottom-right", autoClose: 2000 });
    setIsUpdateLoading(false);
    return;
  }

  // Format oldDate if it exists - it might be a Date object or string
  let formattedOldDate = null;
  if (dateOldEdit) {
    if (dateOldEdit instanceof Date) {
      // If it's a Date object, format it
      const oldDateMoment = moment(dateOldEdit).tz(global.config.getTimeZone());
      if (oldDateMoment.isValid()) {
        formattedOldDate = oldDateMoment.utc().format("YYYY-MM-DD HH:mm:ss");
      }
    } else if (typeof dateOldEdit === 'string') {
      // If it's already a string, validate and use it
      const oldDateMoment = moment.tz(dateOldEdit, global.config.getTimeZone());
      if (oldDateMoment.isValid()) {
        formattedOldDate = oldDateMoment.utc().format("YYYY-MM-DD HH:mm:ss");
      }
    }
  }

  const client = clientEdit;
  // Normalize clientId - extract _id if it's an object, otherwise use as is
  const clientId = clientIdEdit && typeof clientIdEdit === 'object' && clientIdEdit._id 
    ? clientIdEdit._id 
    : clientIdEdit;

  const postData = {
    task: task,
    project: projectIdEdit,
    date: dateString, // Use the explicitly converted string
    oldDate: formattedOldDate,
    tag: tagIdEdit || null,
    billable: billableEdit,
    startTime: startTimeFormated,
    endTime: endTimeFormated,
    duration: uDuration || 0,
    client: client,
    clientId: clientId,
    user: selectedUser,
    id: id,
    tz: global.config.getTimeZone(),
  };

  if (sprintIdEdit) {
    postData.sprint = sprintIdEdit;
  } else {
    postData.sprint = null;
  }

  // Final check: Ensure no NaN values in postData
  const hasNaN = Object.values(postData).some(val =>
    (typeof val === 'number' && isNaN(val)) ||
    (typeof val === 'string' && val.includes('NaN'))
  );

  if (hasNaN) {
    toast("Invalid data detected. Please check all fields.", { position: "bottom-right", autoClose: 2000 });
    setIsUpdateLoading(false);
    return;
  }

  setIsUpdateLoading(true);
  await axios
    .post("/api/time-track/custome-time-update", postData, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
    .then(async (response) => {
      await dispatch(setTimerData(Date.now()));
      setExpandedGroups([]);
      global.config.activityLog(
        window.location.href,
        "Timer",
        `Time Updated Successfully`
      );
      toast("Time Updated Successfully", {
        position: "bottom-right",
        autoClose: 2000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
      setUEndTimeError(false);
      setUStartTimeError(false);
      setIsUpdateLoading(false);
      effectiveSetEditedId(null);
      setTaskNameEdit(null);
      setProjectIdEdit(null);
      setProjectNameEdit(null);
      setSprintIdEdit(null);
      setSprintNameEdit(null);
      setDateEdit(null);
      setStartTimeEdit(TimestampConverter(null));
      setEndTimeEdit(TimestampConverter(null));
      setStartTimeEdit24(TimestampConverter(null));
      setEndTimeEdit24(TimestampConverter(null));
      setClientEdit(null);
      setClientIdEdit(null);
      setBillableEdit(null);
      setTagEdit(null);
      setTagQuery("");
      refetchTimerData();
      effectiveSetEditedId(null);
    })
    .catch((error) => {
      setIsUpdateLoading(false);
      console.error("Error updating timer entry:", error);
      
      // Handle error structure with error field and details
      let errorMessage = "Unable to update timer entry. Please verify your changes and try again.";
      
      if (error.response?.data) {
        const errorData = error.response.data;
        
        // Handle ERROR_BAD_REQUEST format
        if (errorData.error === "ERROR_BAD_REQUEST" && errorData.details) {
          const details = errorData.details;
          // Extract specific validation errors if available
          if (details.detail && details.detail !== "Bad Request") {
            errorMessage = details.detail;
          } else if (details.title && details.title !== "Bad request.") {
            errorMessage = details.title;
          } else {
            errorMessage = "Invalid request. Please check your input and try again.";
          }
        } else if (errorData.message) {
          errorMessage = errorData.message;
        } else if (errorData.error) {
          errorMessage = errorData.error;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage, {
        position: "bottom-right",
        autoClose: 3500,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
      
      // Log to Slack if configured
      if (global.config?.slackMessage) {
        global.config.slackMessage(`Timer Update Error: ${errorMessage}\n${error.toString()}`);
      }
    });
};
